<script setup lang="ts">
    import {useSessionStore} from "@/domains/-session/stores/useSessionStore"
    import {usePageHeader} from "@/components/navigation/usePageHeader"
    import { FlexDirection, Spacing, Align, Justify, Shadow, BorderRadius, Size } from "omind-design-system/tokens";
    import { IconSize } from "omind-design-system/components/odsIcon";
    import { router } from '@/router'
    import {Workspace} from "@/domains/-workspace/models/Workspace.ts";
    import { RouteLocationRaw } from 'vue-router'
    import { odsCard } from "omind-design-system/components/odsCard";
    import { odsLogo } from "omind-design-system/components/odsLogo";
    import { odsBox } from "omind-design-system/components/odsBox";
    import { LogoType } from "omind-design-system/components/odsLogo";
    import { LogoColor } from "omind-design-system/components/odsLogo";
    import ActionMenu from "@/components/actionMenu/ActionMenu.vue";
    import { computed, onMounted, Ref, ref } from "vue";
    import { odsButton } from "omind-design-system/components/odsButton";
    import { ButtonType } from "omind-design-system/components/odsButton";
    import { ButtonColor } from "omind-design-system/components/odsButton";
    import { ButtonSize } from "omind-design-system/components/odsButton";
    import { ActionMenuExposed } from "@/components/actionMenu/ActionMenu";
import { getAccountCreditsRouteLocation } from "@/domains/-account/-credits/routes";
import { getCreditName, totalOrUnlimited } from "@/domains/-account/-credits/functions";
import { isLocaleCategoryLoaded, loadLocales } from "@/i18n";
import { LOCALE_CATEGORIES } from "@/assets/locales/categories";
import { JourneyName } from "@/sdk";
import { usePermissionManager } from "@/domains/-permissions/stores/usePermissionManager";

    const actionMenuRef: Ref<null | HTMLElement & ActionMenuExposed> = ref(null);
    const permissions = usePermissionManager()
    const isLoaded = ref(false)

    const SESSION = useSessionStore()
    const pageHeader = usePageHeader()
    /* pageHeader.setConfig({
        showHome: false,
        showTitle: true,
        breadcrumb: [{
            label: 'test',
            route: 'test'
        },{
            label: 'test2',
            route: 'test2'
        },{
            label: 'test3'
        }]
    }) */
    const breadcrumb = pageHeader.breadcrumb

    const hasCreditTypes = computed(() => {
        if(SESSION.wsp?.workspace)
            return Object.keys(SESSION.wsp?.workspace.credits).length > 0

        return false
    })

    function goHome(){
        if(breadcrumb.segments[0].route)
            router.push(breadcrumb.segments[0].route)
        else
            router.push({name: 'home'})
    }

    function goto(route?: RouteLocationRaw){
        if(route) router.push(route)
    }

    function changeWorkspace(value){
        SESSION.setWorkspace(value.id, router)
    }

    function workspaceTop(workspace: Workspace, listIndex: number){
        SESSION.invertWorkspaces(listIndex, listIndex - 1)
        SESSION.saveWorkspaceOrder()
    }

    function workspaceBottom(workspace: Workspace, listIndex: number){
        SESSION.invertWorkspaces(listIndex, listIndex + 1)
        SESSION.saveWorkspaceOrder()
    }

    

    function redirectToCredits(){
        if(SESSION.wsp?.workspace){
            actionMenuRef.value?.closeAction()
            router.push(getAccountCreditsRouteLocation(SESSION.wsp?.workspace.id))
        }
    }

    function clickCreditButton(){
        if(SESSION.wsp?.workspace.credit_mode === 'per_usage')
            actionMenuRef.value?.openAction()
        else
            redirectToCredits()
    }

    onMounted(async () => {
        await loadLocales(LOCALE_CATEGORIES.MY_ACCOUNT)
        isLoaded.value = true
    })

    const credits = computed(() => {
        const credits = {...SESSION.wsp?.workspace.credits}
        if(credits && Object.hasOwn(credits, 'feta_ascent'))
            delete credits['feta_ascent']
        if(credits && Object.hasOwn(credits, 'feta_meeting_level_2'))
            delete credits['feta_meeting_level_2']
        return credits
    })
</script>

<template>
    <div class="page-header">
        <odsBox
            :padding="Spacing.None"
            :gap="Spacing.Standard"
            :justify="Justify.Start"
        >
                <odsIcon class="home-icon" :class="{show: pageHeader.showHome}" :size="IconSize.Medium" :name="'home'" @click="goHome()"/>
                <span class="separator" :class="{show: pageHeader.showHome}"></span>
                <odsBox
                    :padding="Spacing.None"
                    :gap="Spacing.Small"
                    :justify="Justify.Start"
                    class="breadcrumb"
                    :class="{show: pageHeader.breadcrumb.segments.length > 0}"
                >
                    <template v-for="(segment, index) in breadcrumb.segments">
                        <span :class="{active: segment.route}" v-html="segment.label" @click=goto(segment.route)></span>
                        <span v-if="index < breadcrumb.segments.length - 1"><odsIcon :name="'chevron_right'"/></span>
                    </template>
                </odsBox>
                <odsBox
                    :padding="Spacing.None"
                    :gap="Spacing.Small"
                    :justify="Justify.Start"
                    class="page-title"
                    :class="{show: pageHeader.showTitle && pageHeader.breadcrumb.segments.length === 0}"
                >
                    <h1 v-html="pageHeader.title"></h1>
                </odsBox>
            <odsBox
                :padding="Spacing.None"
                :gap="Spacing.Standard"
                :justify="Justify.Start"
                :width="'auto'"
            >
                <odsBox
                    v-if="isLoaded"
                    :padding="Spacing.None"
                    :justify="Justify.Start"
                    :width="'auto'"
                    style="position: relative;"
                    class="credits-container-header"
                >
                    <odsCard v-if="permissions.canIAccessCredits" class="credits-btn" :radius="BorderRadius.Full" @click="clickCreditButton()">
                        <odsBox
                            :gap="Spacing.Small"
                            :height="'auto'"
                            :width="'auto'"
                        >
                            <odsLogo :type="LogoType.NoText" :color="LogoColor.Light"/>
                            <span v-if="totalOrUnlimited(SESSION.wsp?.workspace.totalAvailableCredits()) === '∞'" v-html="$t('my_account.credits.header_cta.label_unlimited')"></span>
                            <span v-else v-html="$t('my_account.credits.header_cta.label',{count: totalOrUnlimited(SESSION.wsp?.workspace.totalAvailableCredits())},SESSION.wsp?.workspace.totalAvailableCredits()!)"></span>
                        </odsBox>
                    </odsCard>
                    <ActionMenu v-if="permissions.canIAccessCredits && SESSION.wsp?.workspace.credit_mode === 'per_usage'" :visible-backdrop="true" ref="actionMenuRef" class="credit-action-menu" :class="{'right-align': SESSION.workspaces.length <= 1}">
                        <odsBox
                            :padding="Spacing.None"
                            :direction="FlexDirection.Column"
                        >
                            <odsBox
                                :padding="Spacing.Medium"
                                :justify="Justify.SpaceBetween"
                            >
                                <h5 v-html="$t('my_account.credits.popup.title')"></h5>
                                <span>{{ totalOrUnlimited(SESSION.wsp?.workspace.totalAvailableCredits()) }}</span>
                            </odsBox>
                            <div class="credits-separator"></div>
                            <odsBox
                                :direction="FlexDirection.Column"
                                :padding="Spacing.Medium"
                                :justify="Justify.Start"
                                :style="{
                                    maxHeight: '20rem',
                                    overflowY: 'auto'
                                }"
                            >
                                <template v-if="hasCreditTypes">
                                    <odsBox
                                        v-for="(credit, journeyName) in credits"
                                        :justify="Justify.SpaceBetween"
                                        :padding="Spacing.None"
                                        :gap="Spacing.Large"
                                        class="credit-row"
                                        :class="{'credit-option': journeyName === 'feta_ascent' || journeyName === 'feta_meeting_level_2'}"
                                    >
                                        <span v-html="$t(`journey.name.${getCreditName(journeyName)}`)"></span>
                                        <span>{{ totalOrUnlimited(credit?.left) }}</span>
                                    </odsBox>
                                </template>
                                <odsBox v-else>
                                    <span v-html="$t('my_account.credits.popup.empty')"></span>
                                </odsBox>
                            </odsBox>
                            <odsBox
                                :padding="Spacing.Medium"
                                :justify="Justify.SpaceBetween"
                                :style="{paddingTop: 0}"
                            >
                                <odsButton
                                    :type="ButtonType.Outline"
                                    :color="ButtonColor.White"
                                    :label="hasCreditTypes ? $t('my_account.credits.popup.cta') : $t('my_account.credits.popup.cta_empty')"
                                    @click="redirectToCredits"
                                />
                            </odsBox>
                        </odsBox>
                    </ActionMenu>
                </odsBox>
                <!-- <odsIcon :name="'mail'" :size="IconSize.Medium"/>
                <div class="notification-icon">
                    <odsIcon :name="'notifications'" :size="IconSize.Medium"/>
                    <div class="notification-pill"></div>
                </div> -->
            </odsBox>
            <odsDropdown
                v-if="SESSION.workspaces.length > 1"
                :options="SESSION.workspaces"
                :placeholder="'Selectionnez...'"
                :default-value="SESSION.wsp?.workspace"
                @select="changeWorkspace"
            >
                <template #selected-item="slotValue">
                    <div>{{ slotValue.value.name }}</div>
                </template>

                <template #list-item="slotProps">
                    <odsBox
                        :padding="Spacing.None"
                        style="height: 2rem;"
                        class="list-item-workspace"
                    >
                        <odsBox
                            :padding="Spacing.None"
                            :justify="Justify.Start"
                        >
                            {{ slotProps.value.name }}
                        </odsBox>
                        <odsBox
                            :direction="FlexDirection.Column"
                            :padding="Spacing.None"
                            :justify="Justify.SpaceBetween"
                            :width="'auto'"
                        >
                            <odsIcon :name="'arrow_upward'" v-if="slotProps.index > 0" :size="IconSize.Small" @click.stop="workspaceTop(slotProps.value,slotProps.index)"/>
                            <span v-else>&nbsp;</span>
                            <odsIcon :name="'arrow_downward'" v-if="slotProps.index < SESSION.workspaces.length - 1" :size="IconSize.Small" @click.stop="workspaceBottom(slotProps.value,slotProps.index)"/>
                            <span v-else>&nbsp;</span>
                        </odsBox>
                    </odsBox>
                </template>
            </odsDropdown>
        </odsBox>
    </div>
</template>

<style lang="scss">
    .page-header{
        padding: var(--spacing-large) var(--spacing-large);
        position: sticky;
        z-index: 101;
        top: 0;
        background: #FFF;

        &>.ods-box{
            &>*{
                //flex: 1 1 auto;
            }
        }

        h1{
            color: var(--content-label-900);
            font-size: 1.5rem;
            font-weight: 600;
            line-height: 90%;
            letter-spacing: 0.024rem;

            em{
                color: var(--text-body-content);
                font-size: 0.85rem;
                font-style: normal;
                font-weight: 400;
                line-height: 70%;
                letter-spacing: 0.016rem;
                opacity: 0.8;
            }
        }

        .home-icon,.separator{
            opacity: 0;
            transform: translateX(-48px);
            transition-property: opacity, transform;
            transition-duration: 250ms;
            transition-timing-function: ease-out;

            &.show{
                opacity: 1;
                transform: translateX(0);
            }
        }

        .home-icon{
            /* height: 1.1rem;
            flex: 0 0 1.1rem; */
            color: var(--color-purple-500);
            cursor: pointer;
            &.show{
                transition-delay: 250ms;
            }
            
        }

        .separator{
            width: 0.0625rem;
            height: 1.125rem;
            background: #000;
            transition-delay: 250ms;

            &.show{
                opacity: 0.2!important;
                transition-delay: 0ms;
            }
        }

        

        .breadcrumb{
            opacity: 0;
            transform: translateY(48px);
            transition-property: opacity, transform;
            transition-duration: 250ms;
            transition-timing-function: ease-out;

            span{
                color: var(--content-label-900);
                font-size: 0.875rem;
                font-weight: 400;
                line-height: 2rem;
                letter-spacing: 0.014rem;
                opacity: 0.6;
                user-select: none;

                &.active{
                    opacity: 1;
                    text-decoration-line: underline;
                    text-decoration-style: solid;
                    text-decoration-skip-ink: auto;
                    text-decoration-thickness: auto;
                    text-underline-offset: auto;
                    text-underline-position: from-font;
                    cursor: pointer;
                }
            }

            &.show{
                opacity: 1;
                transform: translateY(0);
            }
        }

        .page-title{
            position: absolute;
            width: auto;
            opacity: 0;
            transform: translateY(-48px);
            transition-property: opacity, transform;
            transition-duration: 250ms;
            transition-timing-function: ease-out;
            pointer-events: none;

            &.show{
                opacity: 1;
                transform: translateY(0);
                transition-delay: 500ms;
            }
        }

        .notification-icon{
            position: relative;

            .notification-pill{
                position: absolute;
                background: var(--color-red-500);
                border: solid 2.5px var(--color-neutral-10);
                width: 0.7rem;
                height: 0.7rem;
                border-radius: var(--border-radius-full);
                top: 0.2rem;
                right: 0.05rem;
            }
        }
        .ods-dropdown .ods-dropdown-list .ods-dropdown-item{
            .list-item-workspace{
                .ods-icon{
                    opacity: 0;
                    transition: opacity 150ms ease-out;
                }

                &:hover{
                    .ods-icon{
                        opacity: 1;
                    }
                }
            }
        }

        .credits-btn{
            background: linear-gradient(90deg, #5A18DD 0%, #DC04F0 100%);
            cursor: pointer;

            &>.ods-box{
                padding: var(--spacing-small) var(--spacing-standard);
            }

            .ods-logo{
                height: 1.2rem;
            }

            span{
                white-space: nowrap;
                color: #FAFAFA;
                text-align: center;
                font-size: 0.875rem;
                font-weight: 500;
                line-height: normal;
                letter-spacing: 0.014rem;
            }
        }

        .credit-action-menu{
            z-index: 1000;

            .actions-dropdown{
                border-radius: 1.5rem;
                border: 1px solid rgba(0, 0, 0, 0.08);
                background: #000A29;
                min-width: 15rem;
                top: calc(100% + var(--spacing-small));
                left: 50%;
                right: auto;
                transform: translateX(-50%)!important;

                span{
                    color: #FAFAFA;
                    font-size: 0.75rem;
                    font-weight: 300;
                    line-height: normal;
                    letter-spacing: 0.012rem;
                    white-space: nowrap;

                    &+span{
                        color: #FAFAFA;
                        font-size: 0.75rem;
                        font-weight: 600;
                        line-height: normal;
                        letter-spacing: 0.012rem;
                    }
                }

                h5{
                    color: #FAFAFA;
                    font-size: 1.125rem;
                    font-weight: 600;
                    line-height: normal;
                    letter-spacing: 0.018rem;

                    &+span{
                        color: #FAFAFA;
                        font-size: 0.875rem;
                        font-weight: 300;
                        line-height: normal;
                        letter-spacing: 0.014rem;
                    }
                }

                .credits-separator{
                    width: 100%;
                    height: 0.0625rem;
                    opacity: 0.2;
                    background: #FFF;
                }

                .ods-button{
                    justify-content: center;
                }

                .credit-row{
                    &.credit-option{
                        background-color: rgba(255,255,255,0.15);
                        border-radius: var(--border-radius-full);
                        padding: var(--spacing-xs) var(--spacing-standard);
                        margin-top: -1rem;
                        margin-right: calc(-1 * var(--spacing-standard));

                        span{
                
                            &+span{
                                font-weight: 400;
                            }
                        }
                    }
                }
            }

            &.right-align{
                .actions-dropdown{
                    left: auto;
                    right: 0;
                    transform: translateX(0%)!important;
                }
            }
        }
    }
</style>